import { useState } from "react";
import Api from "../../api/api";
import stringHelpers from "../../../utilities/stringHelpers";
import { ScheduleExportDateRangePicker, isScheduleDateRangeCustomRangeInvalid } from "../scheduleExportDateRangePicker";
import { SearchSelect } from "../../shared/searchSelect";
import { ScheduleExportModal } from "../../shared/scheduleExportModal";
import { TagSelector } from "../../shared/tagSelector";
import arrayUtilities from "../../../utilities/arrayUtilities";

export const paymentsReportType = 19

const defaultScheduleDateSearchMode = 1

export const PaymentsScheduleModal = ({ sites, tags = [], organisationId, token, onCancel, onSaved, scheduledExport, initialParameters }) => {
    const seedParameters = scheduledExport?.parameters ?? initialParameters ?? {}
    const hasCustomDates = seedParameters.from != null || seedParameters.to != null
    const isEditing = !stringHelpers.isNullOrEmpty(scheduledExport?.id)

    const [scheduleFromDate, setScheduleFromDate] = useState(seedParameters.from ?? null)
    const [scheduleToDate, setScheduleToDate] = useState(seedParameters.to ?? null)
    const [scheduledDateRangeTypePreset, setScheduledDateRangeTypePreset] = useState(seedParameters.reportDatePreset || (hasCustomDates ? null : 0))
    const [scheduleSiteId, setScheduleSiteId] = useState(seedParameters.siteId ?? null)
    const [scheduleDateSearchMode, setScheduleDateSearchMode] = useState(seedParameters.dateSearchMode ?? defaultScheduleDateSearchMode)
    const [scheduleSelectedTags, setScheduleSelectedTags] = useState(seedParameters.tagIds ?? [])
    const [isSaving, setIsSaving] = useState(false)
    const [saveErrorMessage, setSaveErrorMessage] = useState(null)

    const isScheduleCustomDateRangeInvalid = isScheduleDateRangeCustomRangeInvalid(scheduledDateRangeTypePreset, scheduleFromDate, scheduleToDate)
    const isSubmitDisabled = isScheduleCustomDateRangeInvalid || isSaving

    const buildScheduledExportParameters = () => {
        const isUsingDateRangePreset = scheduledDateRangeTypePreset !== null
        const dateRangeParameters = isUsingDateRangePreset
            ? { reportDatePreset: scheduledDateRangeTypePreset }
            : { from: scheduleFromDate, to: scheduleToDate }

        return {
            ...dateRangeParameters,
            siteId: scheduleSiteId,
            dateSearchMode: scheduleDateSearchMode,
            tagIds: !arrayUtilities.isNullOrEmpty(scheduleSelectedTags) ? scheduleSelectedTags : null,
        }
    }

    const handleSaveFailed = (error) => {
        setSaveErrorMessage(error?.errorMessage ?? "Unable to save scheduled export")
        setIsSaving(false)
    }

    const handleSaveSucceeded = (data) => {
        if (data?.isError) {
            handleSaveFailed(data)
        } else {
            onSaved()
        }
    }

    return (
        <ScheduleExportModal
            initialValues={ scheduledExport }
            reportType={ paymentsReportType }
            onCancel={ onCancel }
            disableConfirm={ isSubmitDisabled }
            errorMessage={ saveErrorMessage }
            onSubmit={ (isActive, name, scheduleFrequencyMode, monday, tuesday, wednesday, thursday, friday, saturday, sunday, dayOfMonth, time, recipients) => {
                setIsSaving(true)
                setSaveErrorMessage(null)
                if (isEditing) {
                    Api.updateScheduledExport(
                        handleSaveSucceeded,
                        handleSaveFailed,
                        scheduledExport.id,
                        name, paymentsReportType, scheduleFrequencyMode,
                        monday,
                        tuesday,
                        wednesday,
                        thursday,
                        friday,
                        saturday,
                        sunday,
                        time,
                        dayOfMonth,
                        recipients,
                        buildScheduledExportParameters(),
                        isActive,
                        organisationId, token
                    )
                } else {
                    Api.createScheduledExport(
                        handleSaveSucceeded,
                        handleSaveFailed,
                        name, paymentsReportType, scheduleFrequencyMode,
                        monday,
                        tuesday,
                        wednesday,
                        thursday,
                        friday,
                        saturday,
                        sunday,
                        time,
                        dayOfMonth,
                        recipients,
                        buildScheduledExportParameters(),
                        isActive,
                        organisationId, token
                    )
                }
            } }
        >
            <div className="">
                <h5 className="mb-2">Filters</h5>
                <div className="row mb-2">
                    <div className="col-md-3">Date Range</div>
                    <div className="col-md-9">
                        <ScheduleExportDateRangePicker
                            scheduledDateRangeTypePreset={ scheduledDateRangeTypePreset }
                            setScheduledDateRangeTypePreset={ setScheduledDateRangeTypePreset }
                            scheduleFromDate={ scheduleFromDate }
                            setScheduleFromDate={ setScheduleFromDate }
                            scheduleToDate={ scheduleToDate }
                            setScheduleToDate={ setScheduleToDate }
                        />
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-3">Date Mode</div>
                    <div className="col-md-9">
                        <select
                            id="schedule-date-search-mode-selector"
                            value={ scheduleDateSearchMode === null ? "" : scheduleDateSearchMode }
                            onChange={ e => setScheduleDateSearchMode(Number(e.target.value)) }
                            className="border rounded px-2 py-1 w-100"
                        >
                            <option value="0">Strict</option>
                            <option value="1">Allow Overlap</option>
                        </select>
                    </div>
                </div>
                <div className="row mb-2">
                    <div className="col-md-3">Site</div>
                    <div className="col-md-9">
                        <SearchSelect
                            defaultLabel="All"
                            width="100%"
                            options={ [{ value: "", label: "All" }, ...(sites || []).map(item => ({ value: item.id, label: item.name }))] }
                            onSelect={ value => setScheduleSiteId(stringHelpers.isNullOrEmpty(value) ? null : value) }
                            selected={ !stringHelpers.isNullOrEmpty(scheduleSiteId) ? scheduleSiteId : null }
                        />
                    </div>
                </div>
                { !arrayUtilities.isNullOrEmpty(tags) && <div className="row mb-2">
                    <div className="col-md-3">Tags</div>
                    <div className="col-md-9">
                        <TagSelector
                            onChange={ tagSelections => setScheduleSelectedTags(tagSelections) }
                            selectedTags={ scheduleSelectedTags }
                            tags={ tags }
                        />
                    </div>
                </div> }
            </div>
            <hr className="border-dashed border-bottom-0"></hr>
        </ScheduleExportModal>
    )
}
